import { PRIMARY_TRAVELLER_FORM_ID } from 'constants/checkout'
import { RESERVATION_TYPE_BOOK_LATER } from 'constants/reservation'
import { EXPERIENCE_GIFT_SCHEDULE_DATE_LIMIT_BEFORE_EXPIRATION_DATE } from 'constants/experience'
import { max, min } from 'lib/arrayUtils'
import createSelector from 'lib/createSelector'
import {
  isBedbankItem,
  isCruiseItem,
  isInstantBookingLEHotelItem,
  isTourV1Item,
  isTourV2Item,
} from 'lib/checkout'
import { excludeNullOrUndefined } from 'checkout/utils'
import { cartIncludesFlights, getFlightItems, getFlightItemsView } from 'checkout/selectors/view/flights'
import { checkoutAccommodationOfferView, getAccommodationItems, getBundledAccommodationItems } from 'checkout/selectors/view/accommodation'
import { getExperienceItems, getExperienceItemsView, getTransferItemsView } from 'checkout/selectors/view/experience'
import { getInsuranceItems, getInsuranceItemsView } from 'checkout/selectors/view/insurance'
import moment from 'moment'
import { getCarHireItemsView } from 'checkout/selectors/view/carHire'

const defaultCheckoutFormFieldCustomProps: App.Checkout.FormFieldCustomProps = {
  gift: {
    maxScheduleDate: undefined,
  },
}

export const getAccommodationOccupantsFromCart = createSelector(
  getAccommodationItems,
  (items): App.Occupants | undefined => {
    if (items.length) {
      return items.reduce((acc, item) => {
        if ('occupancy' in item) {
          return {
            adults: acc.adults + item.occupancy.adults,
            children: acc.children + (item.occupancy.children || 0),
            infants: acc.infants + (item.occupancy.infants || 0),
          }
        }
        return acc
      }, { adults: 0, children: 0, infants: 0 })
    }
  },
)

export const getLeInstanceBookingItem = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): App.Checkout.InstantBookingLEHotelItem | undefined => items.find(isInstantBookingLEHotelItem),
)

export const getFlightBundledOfferError = createSelector(
  getBundledAccommodationItems,
  (state: App.State) => state.offer.offerErrors,
  (bundledItems, offerErrors) => {
    if (bundledItems.length) {
      const firstItem = bundledItems[0]
      return offerErrors[firstItem.offerId]
    }
  },
)

export const getAccommodationStartDate = createSelector(
  getAccommodationItems,
  (items): string => {
    const startDates = items
      .map(item => {
        if (isInstantBookingLEHotelItem(item) || isBedbankItem(item)) {
          return item.checkIn
        }
        if (isTourV1Item(item) || isTourV2Item(item)) {
          return item.startDate
        }
      })
    return min(startDates, (date) => new Date(date))
  },
)

export const getAccommodationEndDate = createSelector(
  getAccommodationItems,
  (items): string => {
    const startDates = items
      .map(item => {
        if (isInstantBookingLEHotelItem(item) || isBedbankItem(item)) {
          return item.checkOut
        }
        if (isTourV1Item(item) || isTourV2Item(item)) {
          return item.endDate
        }
      })
    return max(startDates, (date) => new Date(date))
  },
)

export const getBundledFlightItem = createSelector(
  getFlightItems,
  (items): App.Checkout.FlightItem | undefined => items.find(item => item.isBundled),
)

export const getBundledFlightReturningBasePrice = createSelector(
  getBundledFlightItem,
  (state: App.State) => state.flights.journeys,
  (item, journeys): number => {
    if (!item) { return 0 }
    const journey = journeys.find(journey => journey.id == item.searchId)
    return journey?.cost || 0
  },
)

export function checkoutAccommodationGroupingKey(item: App.Checkout.AccommodationItem): string {
  if (item.itemType === 'hotel' && item.reservationType === RESERVATION_TYPE_BOOK_LATER) {
    return `${item.offerId}-bnbl-${item.duration}`
  }

  else if (isTourV2Item(item)) {
    return `${item.offerId}-${item.purchasableOption.fkDepartureId}`
  }

  else if (isCruiseItem(item)) {
    return `${item.offerId}-${item.departureId}`
  }

  else if (item.itemType === 'tourV1') {
    return `${item.offerId}-${item.startDate}-${item.endDate}`
  }

  else {
    return `${item.offerId}-${item.checkIn}-${item.checkOut}`
  }
}

export function calculateValue(price: number, discountPercentage: number) {
  const priceFraction = 1 - (discountPercentage / 100)
  if (priceFraction === 0) { return 0 }
  return price / priceFraction
}

export function calculateDiscount(price: number, value: number) {
  if (value === 0) { return 0 }
  const discountFraction = 1 - (price / value)
  return Math.round(100 * discountFraction)
}

export const isAllOffersLoaded = createSelector(
  (state: App.State) => state.checkout.isCartRestored,
  checkoutAccommodationOfferView,
  getExperienceItemsView,
  getInsuranceItemsView,
  (isCartRestored, offerView, experienceItemsView, insuranceItemsView) => {
    return (
      isCartRestored &&
      offerView.data.every(view => view.offerLoaded) &&
      experienceItemsView.hasRequiredData &&
      insuranceItemsView.hasRequiredData
    )
  },
)

export const getPrimaryTravellerForm = createSelector(
  (state: App.State) => state.checkout.form.travellerForms,
  (forms) => forms.find(form => form.id === PRIMARY_TRAVELLER_FORM_ID),
)

export const getAllItemViews = createSelector(
  checkoutAccommodationOfferView,
  getExperienceItemsView,
  getFlightItemsView,
  getInsuranceItemsView,
  getTransferItemsView,
  (...views) => {
    return {
      hasRequiredData: allHaveRequiredData(...views),
      data: {
        accommodationItemsView: views[0],
        experienceItemsView: views[1],
        flightItemsView: views[2],
        insuranceItemsView: views[3],
        transferItemsView: views[4],
      },
    }
  },
)

export const isStandaloneExperience = createSelector(
  (state: App.State) => state.checkout.cart.items,
  getExperienceItems,
  (cartItems, experienceItems): boolean => experienceItems.length === 1 && cartItems.length === 1,
)

export const shouldShowLinkPartnership = createSelector(
  (state: App.State) => state.geo.currentRegionCode,
  getCarHireItemsView,
  getExperienceItemsView,
  checkoutAccommodationOfferView,
  (
    currentRegionCode,
    getCarHireItemsView,
    getExperienceItemsView,
    viewWithStatus,
  ):boolean => {
    if (getCarHireItemsView.data.length) return false
    return !!(currentRegionCode === 'AU' && getExperienceItemsView.data.length) || viewWithStatus.data[0]?.itemViews.some(item => item?.partnership)
  },
)

export const isSingleDesignation = createSelector(
  checkoutAccommodationOfferView,
  getExperienceItemsView,
  getFlightItemsView,
  (checkoutAccommodationOfferView, getExperienceItemsView, getFlightItemsView): boolean => {
    const allItemsViews = [...checkoutAccommodationOfferView.data, ...getExperienceItemsView.data, ...getFlightItemsView.data]
    return [...new Set(allItemsViews.map(view => view?.designation).filter(excludeNullOrUndefined))].length === 1
  },
)

export type AllCheckoutItemViews = ReturnType<typeof getAllItemViews>

function allHaveRequiredData(...objectsWithDataStatuses: Array<App.WithDataStatus<any>>): boolean {
  return objectsWithDataStatuses.every(o => o.hasRequiredData)
}

export const checkoutHasNonAccommodationItems = createSelector(
  getInsuranceItems,
  cartIncludesFlights,
  getExperienceItems,
  (insuranceItems, includesFlights, experienceItems) => {
    return insuranceItems.length > 0 || includesFlights || experienceItems.length > 0
  },
)

export const getCheckoutFormFieldCustomProps = createSelector(
  (state: App.State) => state.checkout.cart.isGift,
  getExperienceItemsView,
  (isGift, experienceItemsView): App.Checkout.FormFieldCustomProps | undefined => {
    const customProps = defaultCheckoutFormFieldCustomProps

    if (isGift) {
      // the user can buy an experience gift only in standalone and is always one experience
      const experienceItem = experienceItemsView.data[0]
      if (experienceItem && experienceItem.isGift) {
        customProps.gift.maxScheduleDate = moment(experienceItem.bookByDate ?? experienceItem.expirationDate).subtract(EXPERIENCE_GIFT_SCHEDULE_DATE_LIMIT_BEFORE_EXPIRATION_DATE, 'days').toDate()
      }
    }

    return customProps
  },
)
